import { useState, useCallback, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useHistory } from 'react-router'
import { useRetailerId, usePartnerId } from '../../../../utils/routing/params.hooks'
import { useCurrentAccountContext } from '../../../../utils/current-account/CurrentAccountContext'
import { useRetailerSatisfactionSurveyMutation } from '../../../../__codegen__/api'
import { useIppToolName } from '../../../../routes/utils/useTotemInfo'
import { ToolSurveyModal } from './ToolSurveyModal'
import {
  canShowSurvey,
  recordSurveySubmitted,
  recordSurveyDismissed,
} from './surveyFrequencyTracker'
import { useToolSurveyAccess } from './useToolSurveyAccess'

const SUCCESS_DISPLAY_MS = 10000

export interface UseToolSurveyOptions {
  /** Workflow slug that triggered the survey (e.g., 'save-collection') */
  workflowSlug: string
  /** Bypass frequency checks (for testing) */
  alwaysShow?: boolean
  /** Automatically trigger survey after specified milliseconds (for time-based triggers) */
  triggerAfterMs?: number
  /** Automatically trigger survey if ?triggerSurvey=true URL param is present (and remove it) */
  triggerOnUrlParam?: boolean
}

export interface UseToolSurveyResult {
  /** Call this to trigger the survey (e.g., after a successful save) */
  triggerSurvey: () => void
  /** The survey modal element - MUST be rendered in your component's JSX (renders via portal to document.body) */
  SurveyModal: React.ReactNode
}

/**
 * Hook for triggering tool surveys from user actions
 *
 * This hook handles everything internally:
 * - Roulette/feature toggle check
 * - Frequency control
 * - Tool name derivation from Totem
 * - GraphQL submission
 * - Modal rendering (via portal to document.body)
 *
 * Usage:
 * ```tsx
 * const { triggerSurvey, SurveyModal } = useToolSurvey({
 *   workflowSlug: 'save-collection',
 * })
 *
 * const handleSave = async () => {
 *   await saveData()
 *   triggerSurvey()
 * }
 *
 * return (
 *   <>
 *     <Button onClick={handleSave}>Save</Button>
 *     {SurveyModal}
 *   </>
 * )
 * ```
 *
 * Or with useUserInitiatedSearchSuccess:
 * ```tsx
 * const { triggerSurvey, SurveyModal } = useToolSurvey({ workflowSlug: 'find-customer-orders' })
 * useUserInitiatedSearchSuccess({ filterParams, searchResponseType, onSuccess: triggerSurvey })
 * // Remember to render {SurveyModal} somewhere in your JSX
 * ```
 *
 * Or with time-based trigger (e.g., after 2 minutes on dashboard):
 * ```tsx
 * const { SurveyModal } = useToolSurvey({
 *   workflowSlug: 'view-dashboard',
 *   triggerAfterMs: 120000,
 * })
 * // Survey will automatically trigger after 2 minutes
 * // Timer clears on unmount
 * return <>{SurveyModal}</>
 * ```
 *
 * Or with URL param trigger (for "Save as Draft" flows that navigate away):
 * ```tsx
 * // On the destination page (e.g., dashboard after Save as Draft)
 * const { SurveyModal } = useToolSurvey({
 *   workflowSlug: 'edit-homepage',
 *   triggerOnUrlParam: true,
 * })
 * // Automatically triggers if URL contains ?triggerSurvey=true and removes the param
 * return <>{SurveyModal}</>
 * ```
 */
export const useToolSurvey = ({
  workflowSlug,
  alwaysShow = false,
  triggerAfterMs,
  triggerOnUrlParam = false,
}: UseToolSurveyOptions): UseToolSurveyResult => {
  const [isTriggered, setIsTriggered] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [shownAt, setShownAt] = useState<string | null>(null)

  // Check if the tool survey feature is enabled via roulette
  const isToolSurveyEnabled = useToolSurveyAccess()

  const { ippToolName, externalId, hasTotemEntity } = useIppToolName()
  const retailerId = useRetailerId({ throwOnMissing: false }) ?? '0'
  const partnerId = usePartnerId({ throwOnMissing: false }) ?? '0'
  const { account } = useCurrentAccountContext()

  const toolDisplayName = ippToolName
  const milestoneSlug = externalId ?? 'unknown-tool'

  const [submitSurvey, { loading: isSubmitting }] = useRetailerSatisfactionSurveyMutation()

  // Handle trigger and frequency check
  useEffect(() => {
    const checkAndShowSurvey = async () => {
      // Early exit if roulette is not enabled
      if (!isToolSurveyEnabled && !alwaysShow) {
        if (isTriggered) {
          setIsTriggered(false)
        }
        return
      }

      // Early exit if no Totem entity exists (legacy routes without ownership)
      // These routes don't have milestone slugs and shouldn't trigger surveys
      if (!hasTotemEntity) {
        if (isTriggered) {
          setIsTriggered(false)
        }
        return
      }

      if (isTriggered && !isOpen && !showSuccess) {
        const result = await canShowSurvey(milestoneSlug, workflowSlug, { alwaysShow })

        if (result.canShow) {
          setIsOpen(true)
          setShownAt(new Date().toISOString())
        }
        // Reset trigger regardless of whether we showed the survey
        setIsTriggered(false)
      }
    }

    checkAndShowSurvey()
  }, [
    isTriggered,
    isOpen,
    showSuccess,
    milestoneSlug,
    workflowSlug,
    alwaysShow,
    isToolSurveyEnabled,
    hasTotemEntity,
  ])

  const triggerSurvey = useCallback(() => {
    setIsTriggered(true)
  }, [])

  // Handle time-based trigger
  useEffect(() => {
    if (triggerAfterMs === undefined || triggerAfterMs <= 0) {
      return
    }

    const timer = setTimeout(() => {
      triggerSurvey()
    }, triggerAfterMs)

    return () => {
      clearTimeout(timer)
    }
  }, [triggerAfterMs, triggerSurvey])

  // Handle URL param trigger (?triggerSurvey=true)
  const location = useLocation()
  const history = useHistory()

  useEffect(() => {
    if (!triggerOnUrlParam) {
      return
    }

    const params = new URLSearchParams(location.search)
    if (params.get('triggerSurvey') === 'true') {
      triggerSurvey()
      // Remove the param to prevent re-triggering on refresh
      params.delete('triggerSurvey')
      history.replace({ search: params.toString() })
    }
  }, [triggerOnUrlParam, location.search, triggerSurvey, history])

  const handleClose = useCallback(async () => {
    if (isOpen && !showSuccess) {
      await recordSurveyDismissed(milestoneSlug, workflowSlug)
    }
    setIsOpen(false)
    setShowSuccess(false)
    setShownAt(null)
  }, [isOpen, showSuccess, milestoneSlug, workflowSlug])

  const handleSubmit = useCallback(
    async (rating: number, feedback: string) => {
      const submittedAt = new Date().toISOString()

      const surveyResponses = [{ surveyQuestion: 'toolRating', surveyResponse: String(rating) }]

      if (feedback.trim()) {
        surveyResponses.push({
          surveyQuestion: 'additionalFeedback',
          surveyResponse: feedback.trim(),
        })
      }

      try {
        await submitSurvey({
          variables: {
            retailerId,
            accountId: account?.id ?? '',
            partnerId,
            urlPath: window.location.pathname,
            surveyResponses,
            shownAt: shownAt ?? submittedAt,
            submittedAt,
            milestoneSlug,
            workflowSlug,
          },
        })

        await recordSurveySubmitted(milestoneSlug, workflowSlug)
        setShowSuccess(true)
      } catch (error) {
        await recordSurveySubmitted(milestoneSlug, workflowSlug)
        console.error('[useToolSurvey] Failed to submit survey:', error)
        handleClose()
      }
    },
    [
      submitSurvey,
      retailerId,
      account?.id,
      partnerId,
      shownAt,
      milestoneSlug,
      workflowSlug,
      handleClose,
    ]
  )

  // Create the modal element - renders via portal to document.body
  const SurveyModal = useMemo(() => {
    const modalElement = (
      <ToolSurveyModal
        toolName={toolDisplayName}
        isOpen={isOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        showSuccess={showSuccess}
        autoCloseAfterSuccessMs={SUCCESS_DISPLAY_MS}
      />
    )

    // Use portal to render at document.body level
    if (typeof document !== 'undefined') {
      return createPortal(modalElement, document.body)
    }

    return modalElement
  }, [toolDisplayName, isOpen, handleClose, handleSubmit, isSubmitting, showSuccess])

  return {
    triggerSurvey,
    SurveyModal,
  }
}
